import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Brain, Heart, Eye, TrendingUp, EyeOff, Loader2, Activity, Stethoscope, ClipboardCheck, Shield, Lock } from 'lucide-react';
import { authAPI } from '../services/api';

const features = [
  {
    id: 1,
    icon: 'Brain',
    title: 'Knowledge Assessment',
    description: 'Measure healthcare knowledge with validated question frameworks and scoring systems'
  },
  {
    id: 2,
    icon: 'Heart',
    title: 'Attitude Analysis',
    description: 'Capture attitudes towards health interventions using Likert scales and semantic differentials'
  },
  {
    id: 3,
    icon: 'Eye',
    title: 'Perception Mapping',
    description: 'Understand patient and provider perceptions with advanced qualitative tools'
  },
  {
    id: 4,
    icon: 'TrendingUp',
    title: 'Market Research Analytics',
    description: 'Deep insights for pharmaceutical and healthcare market research'
  }
];

const AuthPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState(null); // null | 'sending' | 'sent' | 'error'

  // Check if already authenticated
  useEffect(() => {
    const token = localStorage.getItem('token');
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (token && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Call actual API
      const tokenData = await authAPI.login(formData.username, formData.password);
      localStorage.setItem('token', tokenData.access_token);
      
      // Get user info
      const userData = await authAPI.getCurrentUser();
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');
      
      // Redirect based on role
      if (userData.role === 'administrator') {
        navigate('/dashboard');
      } else if (userData.role === 'sponsor' || userData.role === 'agency') {
        navigate('/sponsor');
      } else {
        navigate('/doctor');
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.detail || 'Invalid username or password. Please contact your administrator.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      Brain: <Brain className="w-5 h-5" />,
      Heart: <Heart className="w-5 h-5" />,
      Eye: <Eye className="w-5 h-5" />,
      TrendingUp: <TrendingUp className="w-5 h-5" />
    };
    return icons[iconName] || <Brain className="w-5 h-5" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 px-8 py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">KAP<span className="text-cyan-400">Surv</span></span>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-sm text-slate-400">
            <Shield className="w-4 h-4" />
            <span>Secure Healthcare Research Platform</span>
          </div>
        </div>
      </header>

      <div className="relative z-10 min-h-[calc(100vh-88px)] flex">
        {/* Left Section - Hero Content */}
        <div className="hidden lg:flex flex-1 flex-col justify-center px-16 py-12">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 text-blue-300 rounded-full text-sm font-medium mb-8">
              <Stethoscope className="w-4 h-4" />
              Healthcare Research Platform
            </div>
            
            <h1 className="text-5xl font-bold text-white leading-tight mb-6">
              Powerful <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">KAP Surveys</span> for Healthcare Insights
            </h1>
            
            <p className="text-xl text-slate-300 mb-12 leading-relaxed">
              Design, deploy, and analyze Knowledge, Attitude & Perception surveys tailored for healthcare research and pharmaceutical market studies.
            </p>

            {/* Feature Grid */}
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature) => (
                <div
                  key={feature.id}
                  className="group p-5 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                >
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mb-4 group-hover:from-blue-500/30 group-hover:to-cyan-500/30 transition-colors">
                    <span className="text-cyan-400">{getIcon(feature.icon)}</span>
                  </div>
                  <h3 className="font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>

            {/* Trust Badge */}
            <div className="mt-12 flex items-center gap-2 text-sm text-slate-400">
              <ClipboardCheck className="w-4 h-4 text-emerald-400" />
              <span>Secure Healthcare Research Platform</span>
            </div>
          </div>
        </div>

        {/* Right Section - Login Form */}
        <div className="flex-1 lg:flex-none lg:w-[500px] flex flex-col items-center justify-center px-8 py-12">
          <div className="w-full max-w-sm">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center justify-center gap-3 mb-10">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-blue-500/25">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <span className="text-3xl font-bold text-white">KAP<span className="text-cyan-400">Surv</span></span>
            </div>

            {/* Login Card */}
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 shadow-2xl">
              <div className="text-center mb-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/25">
                  <Lock className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Welcome Back</h2>
                <p className="text-slate-400">Sign in to access your research dashboard</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-slate-300 font-medium">
                    Username or Email
                  </Label>
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    placeholder="Enter your username or email"
                    value={formData.username}
                    onChange={handleInputChange}
                    className="h-12 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-cyan-400 focus:ring-cyan-400/20 transition-all duration-200 rounded-xl"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-300 font-medium">
                      Password
                    </Label>
                    <button type="button" onClick={() => { setShowForgotPassword(true); setResetStatus(null); setResetEmail(''); }} className="text-sm text-cyan-400 hover:text-cyan-300 font-medium transition-colors" data-testid="forgot-password-link">
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="h-12 pr-12 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-cyan-400 focus:ring-cyan-400/20 transition-all duration-200 rounded-xl"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white font-semibold rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-cyan-500/25"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-center text-sm text-slate-400">
                  Need access? Contact your organization administrator to get your login credentials.
                </p>
              </div>
            </div>

            <p className="text-center text-xs text-slate-500 mt-6">
              Contact your administrator for login credentials
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 px-8 py-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm text-slate-500">
          <p>© 2025 KAPSurv. All rights reserved.</p>
        </div>
      </footer>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md p-6 space-y-5">
            <div className="text-center">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-white text-xl font-semibold">Reset Password</h2>
              <p className="text-slate-400 text-sm mt-1">Enter your email address and we'll send you a reset link</p>
            </div>

            {resetStatus === 'sent' ? (
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-center">
                <p className="text-emerald-400 font-medium">Reset link sent!</p>
                <p className="text-slate-400 text-sm mt-1">Check your email inbox for the password reset link.</p>
              </div>
            ) : (
              <form onSubmit={async (e) => {
                e.preventDefault();
                setResetStatus('sending');
                try {
                  await authAPI.forgotPassword(resetEmail);
                  setResetStatus('sent');
                } catch (err) {
                  setResetStatus('error');
                }
              }} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Email Address</Label>
                  <Input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="Enter your registered email"
                    className="h-12 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-cyan-400 rounded-xl"
                    required
                    data-testid="reset-email-input"
                  />
                </div>
                {resetStatus === 'error' && (
                  <p className="text-red-400 text-sm">Something went wrong. Please try again.</p>
                )}
                <Button
                  type="submit"
                  disabled={resetStatus === 'sending'}
                  className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 rounded-xl font-semibold"
                  data-testid="send-reset-link-btn"
                >
                  {resetStatus === 'sending' ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                  Send Reset Link
                </Button>
              </form>
            )}

            <button
              onClick={() => setShowForgotPassword(false)}
              className="w-full text-sm text-slate-400 hover:text-slate-300 transition-colors py-2"
            >
              Back to Login
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthPage;
